import React, { useState, useEffect, useRef, useCallback } from "react";
import { fetchGeneratedNotes } from "../../services/firebase";
import PatientService from "../../services/apiservice";
import DynamicTable from "../../components/DynamicTable";
import NoteModal from "../../components/NoteModal";
import DocumentsModal from "../../components/DocumentsModal";
import Button from "../../components/Button";
import SearchableDropdown from "../../components/SearchableDropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./styles.module.css";

const GeneratedNotesPage = () => {
  // Data state
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [physiotherapists, setPhysiotherapists] = useState([]);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNoteForDocuments, setSelectedNoteForDocuments] = useState(null);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const notesPerPage = 10;

  // Store cursors for each page (Map: pageNumber -> lastVisible doc)
  const cursorsRef = useRef(new Map());

  // Filters state
  const [filters, setFilters] = useState({
    type: "",
    noteTypeId: "",
    patient_id: null,
    campus_id: null,
    physiotherapist_id: null,
    dateFilters: {
      created: { filter: "", start: null, end: null },
      updated: { filter: "", start: null, end: null },
    },
  });

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "Consultation", label: "Consultation" },
    { value: "Treatment", label: "Treatment" },
    { value: "Assessment", label: "Assessment" },
  ];

  // Assessment note types (only shown when type === "Assessment")
  const assessmentNoteTypeOptions = [
    { value: "", label: "All Assessment Types" },
    { value: "Tj3ywilJzZ73VRoWG3FW", label: "MSK Assessment" },
    { value: "Uuud3Lw3gWv1afW6UmgK", label: "Stroke Assessment" },
    { value: "y2OQ1Y3DV3yoqjuQA9qZ", label: "Brain Related Injury" },
    { value: "kaB1oKHu8rGOjxAD33ac", label: "Spinal & Related Disorders" },
    { value: "99Bge518bCLJkD8MCErp", label: "CNS demyelination disorders" },
    { value: "8FDngjCr8scK5C37LcAM", label: "Bell's Palsy" },
    { value: "1wzPwcJRmEYlkrkCicoM", label: "Nerve Related disorders" },
    { value: "0fO1eaTHPL6dMGzxHarD", label: "Parkinsons & Related disorders" },
    { value: "RrU8wQRnYSUHsootHloy", label: "S & C Assessment" },
    { value: "DmkTLh0Xr24N1HkISVrH", label: "MSK extension assessment" },
    { value: "s22xWyi0v5dwBuChv3SV", label: "NDI" },
    { value: "qHMLEappUbXhquAYWThu", label: "ODI" },
    { value: "zGddn4aoQVqlGDmDiJoJ", label: "WOMAC" },
    { value: "TCVRFTRkCT8HkSDvBME0", label: "QuickDASH" },
  ];

  const dateFilterOptions = [
    { value: "", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "this_week", label: "This Week" },
    { value: "this_month", label: "This Month" },
    { value: "custom", label: "Custom Range" },
  ];

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / notesPerPage));

  // Format timestamp for display
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return "N/A";
    if (typeof timestamp === "string") return timestamp;
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    if (timestamp instanceof Date) return timestamp.toLocaleString();
    return "N/A";
  }, []);

  // Get date range from filter preset
  const getDateRange = useCallback((filter) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case "today": {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return { start: today, end: tomorrow };
      }
      case "this_week": {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 7);
        return { start: startOfWeek, end: endOfWeek };
      }
      case "this_month": {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
        return { start: startOfMonth, end: endOfMonth };
      }
      default:
        return { start: null, end: null };
    }
  }, []);

  // Build API filters from state
  const buildApiFilters = useCallback(() => {
    const apiFilters = {
      type: filters.type || undefined,
      patient_id: filters.patient_id,
      campus_id: filters.campus_id,
      physiotherapist_id: filters.physiotherapist_id,
      dateFilters: {},
    };

    // Add noteTypeId filter when type is Assessment
    if (filters.type === "Assessment" && filters.noteTypeId) {
      apiFilters.noteTypeId = filters.noteTypeId;
    }

    ["created", "updated"].forEach((dateType) => {
      const { filter, start, end } = filters.dateFilters[dateType];

      if (filter && filter !== "custom") {
        const range = getDateRange(filter);
        if (range.start && range.end) {
          apiFilters.dateFilters[dateType] = range;
        }
      } else if (filter === "custom" && start && end) {
        apiFilters.dateFilters[dateType] = { start, end };
      }
    });

    return apiFilters;
  }, [filters, getDateRange]);

  // Fetch notes from Firebase
  const fetchNotes = useCallback(
    async (page = 1, resetCursors = false) => {
      setIsLoading(true);
      setError(null);

      try {
        const apiFilters = buildApiFilters();

        // Reset cursors if filters changed
        if (resetCursors) {
          cursorsRef.current = new Map();
        }

        // Get cursor for requested page (page 1 has no cursor)
        const startAfterDoc = page > 1 ? cursorsRef.current.get(page) : null;

        // If missing cursor for page > 1, reset to page 1
        if (page > 1 && !startAfterDoc) {
          console.warn(`Missing cursor for page ${page}, resetting to page 1`);
          return fetchNotes(1, true);
        }

        const result = await fetchGeneratedNotes(apiFilters, {
          pageSize: notesPerPage,
          startAfterDoc,
        });

        // Format notes for display
        const formattedNotes = result.notes.map((note) => ({
          ...note,
          created: formatTimestamp(note.created),
          updated: formatTimestamp(note.updated),
        }));

        // Store cursor for next page
        if (result.lastVisible) {
          cursorsRef.current.set(page + 1, result.lastVisible);
        }

        setNotes(formattedNotes);
        setTotalCount(result.totalCount);
        setHasMore(result.hasMore);
        setCurrentPage(page);
      } catch (err) {
        console.error("Failed to fetch notes:", err);
        console.error("Error details:", {
          message: err.message,
          code: err.code,
          stack: err.stack,
        });
        
        // Show more helpful error messages
        let errorMessage = err.message || "Failed to fetch notes";
        
        if (err.code === "failed-precondition") {
          errorMessage = "Missing Firestore index. Check console for index creation link.";
        } else if (err.message?.includes("index")) {
          errorMessage = "Index error: " + err.message;
        }
        
        setError(errorMessage);
        setNotes([]);
      } finally {
        setIsLoading(false);
      }
    },
    [buildApiFilters, formatTimestamp]
  );

  // Fetch dropdown data
  const fetchPatients = async () => {
    try {
      const patientData = await PatientService.getPatients();
      const options = patientData.map((p) => ({
        value: p.id,
        label: p.identification_code
          ? `${p.name} (${p.identification_code})`
          : p.name,
      }));
      setPatients([{ value: null, label: "All Patients" }, ...options]);
    } catch (err) {
      console.error("Failed to fetch patients:", err);
    }
  };

  const fetchPhysiotherapists = async () => {
    try {
      const data = await PatientService.fetchPhysiotherapists();
      const options = data.map((p) => ({ value: p.id, label: p.name }));
      setPhysiotherapists([{ value: null, label: "All Physiotherapists" }, ...options]);
    } catch (err) {
      console.error("Failed to fetch physiotherapists:", err);
    }
  };

  const fetchCampusList = async () => {
    try {
      const data = await PatientService.fetchCampusList();
      const options = data.map((c) => ({ value: c.id, label: c.name }));
      setCampuses([{ value: null, label: "All Campuses" }, ...options]);
    } catch (err) {
      console.error("Failed to fetch campuses:", err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchPatients();
    fetchCampusList();
    fetchPhysiotherapists();
  }, []);

  // Fetch notes when filters change
  useEffect(() => {
    fetchNotes(1, true);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage || isLoading) {
      return;
    }
    fetchNotes(newPage);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      const updates = { ...prev };
      updates[filterType] =
        ["patient_id", "campus_id", "physiotherapist_id"].includes(filterType)
          ? value ? parseInt(value, 10) : null
          : value;

      // Clear noteTypeId when type changes away from Assessment
      if (filterType === "type" && value !== "Assessment") {
        updates.noteTypeId = "";
      }
      return updates;
    });
  };

  const handleDateFilterChange = (dateType, filterValue) => {
    const range = filterValue === "custom" ? { start: null, end: null } : getDateRange(filterValue);

    setFilters((prev) => ({
      ...prev,
      dateFilters: {
        ...prev.dateFilters,
        [dateType]: { filter: filterValue, start: range.start, end: range.end },
      },
    }));
  };

  const handleCustomDateChange = (dateType, field, date) => {
    setFilters((prev) => ({
      ...prev,
      dateFilters: {
        ...prev.dateFilters,
        [dateType]: { ...prev.dateFilters[dateType], [field]: date },
      },
    }));
  };

  // Modal handlers
  const handleViewNote = (note) => {
    setSelectedNote(note);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNote(null);
  };

  const handleViewDocuments = (note) => {
    setSelectedNoteForDocuments(note);
    setIsDocumentsModalOpen(true);
  };

  const closeDocumentsModal = () => {
    setIsDocumentsModalOpen(false);
    setSelectedNoteForDocuments(null);
  };

  // Table config - show Assessment Note Type & Validated only when filtering by Assessment
  const assessmentColumns =
    filters.type === "Assessment"
      ? [
          { key: "noteTypeName", label: "Assessment Note Type" },
          { key: "isValidated", label: "Validated" },
        ]
      : [];
  const headers = [
    { key: "id", label: "ID" },
    { key: "type", label: "Type" },
    ...assessmentColumns,
    { key: "patient_name", label: "Patient Name" },
    { key: "physiotherapistName", label: "Physiotherapist" },
    { key: "campusName", label: "Campus" },
    { key: "created", label: "Created" },
    { key: "updated", label: "Updated" },
    { key: "viewNote", label: "Actions" },
  ];

  const tableData = notes.map((note) => ({
    ...note,
    noteTypeName: note.noteTypeName || "-",
    isValidated: note.isValidated === true ? "Yes" : "No",
    viewNote: (
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <Button onClick={() => handleViewNote(note)}>View Note</Button>
        {note.type === "Assessment" && (
          <Button onClick={() => handleViewDocuments(note)}>See Documents</Button>
        )}
      </div>
    ),
  }));

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Generated Notes</h1>

      {/* Error Banner */}
      {error && (
        <div className={styles.errorBanner}>
          <p>{error}</p>
          <button onClick={() => fetchNotes(1, true)}>Retry</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filterContainer}>
        <div className={styles.filterGroup}>
          <SearchableDropdown
            options={typeOptions}
            value={filters.type}
            onChange={(value) => handleFilterChange("type", value)}
            placeholder="Select Type"
            label="Filter by Type"
          />
          {filters.type === "Assessment" && (
            <SearchableDropdown
              options={assessmentNoteTypeOptions}
              value={filters.noteTypeId || ""}
              onChange={(value) => handleFilterChange("noteTypeId", value)}
              placeholder="Select Assessment Type"
              label="Filter by Assessment Type"
            />
          )}
          <SearchableDropdown
            options={patients}
            value={filters.patient_id !== null ? filters.patient_id.toString() : ""}
            onChange={(value) => handleFilterChange("patient_id", value)}
            placeholder="Select Patient"
            label="Filter by Patient"
          />
          <SearchableDropdown
            options={campuses}
            value={filters.campus_id !== null ? filters.campus_id.toString() : ""}
            onChange={(value) => handleFilterChange("campus_id", value)}
            placeholder="Select Campus"
            label="Filter by Campus"
          />
          <SearchableDropdown
            options={physiotherapists}
            value={filters.physiotherapist_id !== null ? filters.physiotherapist_id.toString() : ""}
            onChange={(value) => handleFilterChange("physiotherapist_id", value)}
            placeholder="Select Physiotherapist"
            label="Filter by Physiotherapist"
          />
        </div>

        <div className={styles.filterGroup}>
          {["created", "updated"].map((dateType) => (
            <div key={dateType} className={styles.dateFilterGroup}>
              <SearchableDropdown
                options={dateFilterOptions}
                value={filters.dateFilters[dateType].filter}
                onChange={(value) => handleDateFilterChange(dateType, value)}
                placeholder={`${dateType.charAt(0).toUpperCase() + dateType.slice(1)} Date`}
                label={`${dateType.charAt(0).toUpperCase() + dateType.slice(1)} Date Filter`}
              />
              {filters.dateFilters[dateType].filter === "custom" && (
                <div className={styles.customDatePicker}>
                  <DatePicker
                    selected={filters.dateFilters[dateType].start}
                    onChange={(date) => handleCustomDateChange(dateType, "start", date)}
                    selectsStart
                    startDate={filters.dateFilters[dateType].start}
                    endDate={filters.dateFilters[dateType].end}
                    placeholderText="Start Date"
                    className={styles.datePicker}
                  />
                  <DatePicker
                    selected={filters.dateFilters[dateType].end}
                    onChange={(date) => handleCustomDateChange(dateType, "end", date)}
                    selectsEnd
                    startDate={filters.dateFilters[dateType].start}
                    endDate={filters.dateFilters[dateType].end}
                    minDate={filters.dateFilters[dateType].start}
                    placeholderText="End Date"
                    className={styles.datePicker}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Results Info */}
      <div className={styles.resultsInfo}>
        Showing {notes.length} of {totalCount} notes
        {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
      </div>

      {/* Table */}
      <DynamicTable
        headers={headers}
        data={tableData}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        title="Generated Notes"
        onRowClick={() => {}}
      />

      {/* Modals */}
      {selectedNote && (
        <NoteModal isOpen={isModalOpen} onClose={closeModal} note={selectedNote} />
      )}
      <DocumentsModal
        isOpen={isDocumentsModalOpen}
        onClose={closeDocumentsModal}
        note={selectedNoteForDocuments}
      />
    </div>
  );
};

export default GeneratedNotesPage;